/**
 * MobyCalendar™ — CalendarPanel
 * 
 * Month grid calendar rendered in the main content area.
 * Wired via CustomEvent dispatch (same pattern as EmailViewPanel / AVStreamPanel).
 * 
 * - Click day → dispatches 'calendar-day-open' with date
 * - Navigation: prev month | Today | next month
 * - Events shown as colored pills in day cells
 */

import { useState, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { useCalendarEvents } from '@/hooks/useCalendarEvents';
import type { CalendarEvent } from '@/lib/calendar-types';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Get the first day of a month (Date at midnight local) */
function startOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1);
}

/** Get the last day of a month */
function endOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0, 23, 59, 59, 999);
}

/** Build a 6-row × 7-col grid of dates for the month view (includes padding days) */
function buildMonthGrid(year: number, month: number): Date[] {
  const first = startOfMonth(year, month);
  const firstDow = first.getDay(); // 0=Sun
  const gridStart = new Date(year, month, 1 - firstDow);

  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    days.push(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
  }
  return days;
}

/** Check if two dates are the same calendar day */
function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

/** Group events by day-of-month key "YYYY-MM-DD" */
function groupEventsByDay(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  for (const ev of events) {
    const d = new Date(ev.startTime);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const arr = map.get(key) ?? [];
    arr.push(ev);
    map.set(key, arr);
  }
  return map;
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ─── Default event colors ───────────────────────────────────────────────────

const DEFAULT_COLORS = [
  'bg-primary',
  'bg-tertiary',
  'bg-secondary',
  'bg-error',
];

function eventColorClass(ev: CalendarEvent, idx: number): string {
  if (ev.color) return ''; // handled via inline style
  return DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
}

// ─── Component ──────────────────────────────────────────────────────────────

export function CalendarPanel() {
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  // Query range: full month + padding week on each side for edge rendering
  const rangeStart = useMemo(() => {
    const d = startOfMonth(viewYear, viewMonth);
    d.setDate(d.getDate() - 7);
    return d.getTime();
  }, [viewYear, viewMonth]);

  const rangeEnd = useMemo(() => {
    const d = endOfMonth(viewYear, viewMonth);
    d.setDate(d.getDate() + 7);
    return d.getTime();
  }, [viewYear, viewMonth]);

  const events = useCalendarEvents(rangeStart, rangeEnd);
  const eventsByDay = useMemo(() => groupEventsByDay(events), [events]);
  const grid = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const goToPrevMonth = useCallback(() => {
    setViewMonth(m => {
      if (m === 0) { setViewYear(y => y - 1); return 11; }
      return m - 1;
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    setViewMonth(m => {
      if (m === 11) { setViewYear(y => y + 1); return 0; }
      return m + 1;
    });
  }, []);

  const goToToday = useCallback(() => {
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
  }, []);

  const handleDayClick = useCallback((date: Date) => {
    window.dispatchEvent(new CustomEvent('calendar-day-open', {
      detail: { date: date.getTime() },
    }));
  }, []);

  return (
    <div className="bg-surface rounded-t-large relative flex flex-1 flex-col overflow-hidden sm:mr-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-outline-variant">
        <div className="flex items-center gap-2">
          <CalendarDays size={20} className="text-primary" />
          <h2 className="text-title-lg text-on-surface">
            {MONTH_NAMES[viewMonth]} {viewYear}
          </h2>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={goToPrevMonth}
            className="p-1.5 rounded-full hover:bg-on-surface/8 text-on-surface-variant transition"
            title="Previous month"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={goToToday}
            className="px-3 py-1 rounded-full text-label-md text-primary hover:bg-primary/8 transition"
          >
            Today
          </button>
          <button
            onClick={goToNextMonth}
            className="p-1.5 rounded-full hover:bg-on-surface/8 text-on-surface-variant transition"
            title="Next month"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-b border-outline-variant">
        {WEEKDAY_LABELS.map(label => (
          <div key={label} className="py-2 text-center text-label-sm text-on-surface-variant">
            {label}
          </div>
        ))}
      </div>

      {/* Month grid — 6 rows × 7 cols */}
      <div className="grid grid-cols-7 flex-1 auto-rows-fr overflow-y-auto">
        {grid.map((date, i) => {
          const inMonth = date.getMonth() === viewMonth;
          const isToday = isSameDay(date, today);
          const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
          const dayEvents = eventsByDay.get(key) ?? [];

          return (
            <button
              key={i}
              onClick={() => handleDayClick(date)}
              className={`
                relative flex flex-col items-start p-1 border-b border-r border-outline-variant/50
                text-left transition hover:bg-on-surface/5 min-h-[60px] md:min-h-[80px]
                ${!inMonth ? 'opacity-40' : ''}
              `}
            >
              {/* Day number */}
              <span
                className={`
                  text-body-sm md:text-body-md w-6 h-6 flex items-center justify-center rounded-full
                  ${isToday ? 'bg-primary text-on-primary font-bold' : 'text-on-surface'}
                `}
              >
                {date.getDate()}
              </span>

              {/* Event pills (max 3 visible, +N overflow) */}
              <div className="w-full mt-0.5 space-y-0.5 overflow-hidden flex-1">
                {dayEvents.slice(0, 3).map((ev, idx) => (
                  <div
                    key={ev.id}
                    className={`
                      truncate rounded-sm px-1 text-[10px] md:text-label-sm leading-tight
                      ${ev.color ? 'text-white' : `${eventColorClass(ev, idx)} text-on-primary`}
                    `}
                    style={ev.color ? { backgroundColor: ev.color } : undefined}
                    title={ev.title}
                  >
                    {ev.allDay ? ev.title : `${new Date(ev.startTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} ${ev.title}`}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[10px] text-on-surface-variant px-1">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
